import classNames from "classnames";
import { useFormik } from "formik";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../../components/buttons/button";
import { ContainerCard } from "../../../../../components/cards/containerCard";
import { InformationCard } from "../../../../../components/cards/infoCard";
import { TextInput } from "../../../../../components/inputs/textInput";
import { TextInputViewOnly } from "../../../../../components/inputs/textInputViewOnly";
import { UploadedFileView } from "../../../../../components/inputs/uploadedFileView";
import { UploadCertificateModal } from "../../../../../components/modals/app_modals/uploadCertificateModal";
import { UploadConstitutionModal } from "../../../../../components/modals/app_modals/uploadConstitutionModal";
import { ProfilePicture } from "../../../../../components/profile/profilePicture";
import { doNotification } from "../../../../../components/toast/nofitication";
import { PageHeader } from "../../../../../components/typography/pageHeader";
import { useModalControl } from "../../../../../customHooks/useModalControl";
import { decodeUrl, getTextFromStatus } from "../../../../../helperFunctions";
import { useGetUserProfileQuery } from "../../../../../redux/features/auth/auth.api";
import {
  useUpdateCooperativeDescriptionMutation,
  useUploadCooperativeLogoMutation,
} from "../../../../../redux/features/cooperative/cooperative.api";
import { useAppSelector } from "../../../../../redux/hooks";

export const CooperativeProfileEdit = () => {
  const [uploadedPicture, setUploadedPicture] = useState<File | null>(null);

  const profilePicRef = useRef<HTMLInputElement | null>(null);

  const { profile } = useAppSelector((state) => state.auth);

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const [uploadLogo, { isLoading: uploadingLogo }] =
    useUploadCooperativeLogoMutation();

  const [updateDescription, { isLoading: updatingDescription }] =
    useUpdateCooperativeDescriptionMutation();

  const { refetch: refetchProfile } = useGetUserProfileQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const navigate = useNavigate();

  const {
    values,
    errors,
    isValid,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
  } = useFormik({
    initialValues: {
      description: profile?.description || "",
    },
    enableReinitialize: true,
    validationSchema: undefined,
    onSubmit: async (data) => {
      const payload = {
        desc: data?.description,
      };

      const resp = await updateDescription(payload);

      if (resp.data?.success) {
        refetchProfile();
      }
    },
  });

  return (
    <div className="w-full relative">
      <PageHeader
        hasBack={true}
        onBackClick={() => navigate("/app/settings?tab=profile")}
        pageTitle={"Edit Cooperative Profile"}
        className="max-md:hidden"
      />

      <div className="md:mt-[28px] flex flex-row flex-wrap width-lim:flex-nowrap gap-2 md:gap-6">
        <ContainerCard className="!px-0 w-full width-lim:max-w-[655px] order-last width-lim:order-first">
          <div className="px-[28px] border-b border-pry-dark-green-300/5 pb-[20px]">
            <TextInputViewOnly
              className="!px-0 !py-0"
              text="Cooperative Description"
              subtext="Tell us something about your cooperative"
              hasIcon={false}
              plain
            />

            <TextInput
              containerClassName="mt-4 w-full"
              type="textarea"
              name="description"
              value={values?.description}
              placeholder="A brief overview of your cooperative"
              onChange={handleChange}
              onBlur={handleBlur}
              hasIcon={false}
              iconSvg=""
              errors={
                errors?.description && touched?.description
                  ? errors?.description
                  : ""
              }
            />

            <Button
              text={"Save"}
              onClick={() => {
                handleSubmit();
              }}
              type="button"
              className="!min-w-[80px] !max-w-fit mt-2 ml-auto"
              size="small"
              loading={updatingDescription}
              disabled={updatingDescription || !isValid}
              icon={undefined}
              variation="primary"
            />
          </div>

          <div
            className="
            px-[28px] py-[20px]
            border-b border-pry-dark-green-300/5
            flex items-center justify-between
            flex-wrap gap-4
            "
          >
            <div className="md:flex-1">
              <TextInputViewOnly
                className="!px-0 !py-0 max-w-fit"
                text="Certificate of Registration"
                subtext="Upload your cooperative certificate of registration"
                hasIcon={false}
                plain
              />
              {profile?.Certificate && (
                <UploadedFileView
                  viewLink={`${decodeUrl(profile?.Certificate)}`}
                  text={"View certificate"}
                  className="mt-3"
                />
              )}
            </div>

            {profile?.Certificate ? (
              <p
                className={classNames(
                  "py-1 border w-fit h-fit border-neut-300 px-2 rounded-3xl font-medium text-xs",
                  {
                    "text-red-200 bg-red-50": [
                      "rejected",
                      "",
                      undefined,
                    ].includes(profile?.Certificatestatus),
                    "text-gold-200 bg-gold-200/5": [
                      "pending",
                      "submitted",
                    ].includes(profile?.Certificatestatus),
                    "text-pry-green-200 bg-[#F1F9F6]": [
                      "active",
                      "approved",
                    ].includes(profile?.Certificatestatus),
                  },
                )}
              >{`• ${getTextFromStatus(profile?.Certificatestatus)}`}</p>
            ) : undefined}

            {[null, undefined, "rejected"].includes(
              profile?.Certificatestatus,
            ) || !profile?.Certificate ? (
              <Button
                text={profile?.Certificate ? "Re-upload" : "Upload"}
                onClick={() => {
                  handleModalOpen("uploadCertificateModal");
                }}
                type="button"
                className="!min-w-[80px] !max-w-fit max-md:ml-auto"
                size="small"
                loading={false}
                disabled={false}
                variation="secondary"
              />
            ) : undefined}
          </div>

          <div
            className="
              px-[28px] 
              border-b border-pry-dark-green-300/5
              py-[20px]
              flex items-center justify-between
              flex-wrap gap-4
              "
          >
            <div className="">
              <TextInputViewOnly
                className="!px-0 !py-0 max-w-fit"
                text="Bye Laws or Constitution"
                subtext="Upload cooperative bye laws or constitution"
                hasIcon={false}
                plain
              />
              {profile?.byelaw && (
                <UploadedFileView
                  viewLink={`${decodeUrl(profile?.byelaw)}`}
                  text="View document"
                  className="mt-3"
                />
              )}
            </div>

            <Button
              text={profile?.byelaw ? "Update" : "Upload"}
              onClick={() => {
                handleModalOpen("uploadConstitutionModal");
              }}
              type="button"
              className="!min-w-[80px] !max-w-fit ml-auto"
              size="small"
              loading={false}
              disabled={false}
              variation="secondary"
            />
          </div>

          <div className="px-[28px] py-[20px]">
            <TextInputViewOnly
              className="!px-0 !py-0"
              text="Email Address"
              subtext={profile?.CoopEmail}
              hasIcon={false}
              plain
            />
          </div>
        </ContainerCard>

        <div className="flex-1 width-lim:sticky-top h-fit">
          <InformationCard
            info={
              <p>
                To change your{" "}
                <span className="text-pry-green-200 font-medium">
                  email address
                </span>
                , please reach out to support.
              </p>
            }
          />

          <ContainerCard
            className="
            flex flex-col items-center justify-center
            mt-4 gap-6
            "
          >
            <ProfilePicture
              className="
              !w-[106px] !h-[106px] 
              !text-2xl !font-bold
              "
              tempSrc={
                uploadedPicture
                  ? URL.createObjectURL(uploadedPicture)
                  : undefined
              }
            />

            <input
              type="file"
              accept="image/*"
              className="hidden"
              ref={profilePicRef}
              multiple={false}
              onChange={(e) => {
                const files = (e.target as HTMLInputElement).files;
                setUploadedPicture(files && files.length > 0 ? files[0] : null);
              }}
            />

            <div className="w-full items-center justify-center flex flex-row flex-wrap gap-2">
              {uploadedPicture && (
                <Button
                  text="Change"
                  onClick={() => {
                    profilePicRef?.current?.click();
                  }}
                  type="button"
                  className="!min-w-[80px] !max-w-fit"
                  size="small"
                  loading={false}
                  disabled={false}
                  variation="secondary"
                />
              )}

              <Button
                text={
                  uploadedPicture
                    ? "Save logo"
                    : `${profile?.logo ? "Change logo" : "Add logo"}`
                }
                onClick={async () => {
                  if (!uploadedPicture) {
                    profilePicRef?.current?.click();
                  } else {
                    const formData = new FormData();
                    formData.append("file", uploadedPicture as Blob);

                    const resp = await uploadLogo(formData);

                    if (resp.data?.success) {
                      if (profile?.logo) {
                        doNotification("Logo changed successfully", "success");
                      } else {
                        doNotification("Logo added successfully", "success");
                      }
                      setUploadedPicture(null);
                      refetchProfile();
                    }
                  }
                }}
                type="button"
                className="!min-w-[80px] !max-w-fit"
                size="small"
                loading={uploadingLogo}
                disabled={uploadingLogo}
                variation={uploadedPicture ? "primary" : "secondary-light"}
              />
            </div>

            <div className="w-full max-w-[353px] text-center">
              <p className="font-bold text-base">
                Help members to identify your cooperative easily
              </p>

              <p className="mt-1 text-pry-dark-green-300/60">
                Your logo will be visible to your members on ThriftCorp
              </p>
            </div>
          </ContainerCard>
        </div>
      </div>

      <UploadCertificateModal
        modalOpen={modalOpen && modal === "uploadCertificateModal"}
        onModalClose={() => {
          handleModalClose();
        }}
      />

      <UploadConstitutionModal
        modalOpen={modalOpen && modal === "uploadConstitutionModal"}
        onModalClose={() => {
          handleModalClose();
        }}
      />
    </div>
  );
};
